import { SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

/**
 * An editable field over a live server value (DD-3231). It shows the server value until the
 * user types; from then on it shows the draft, so a colleague's save arriving through the
 * cache never overwrites unsaved typing. The draft clears once the server value matches it
 * (your own save came back) or on `discard`.
 */
export const useDraftState = <T>(serverValue: T) => {
    const [draft, setDraft] = useState<{ value: T } | null>(null);
    const serverValueRef = useRef(serverValue);
    serverValueRef.current = serverValue;

    useEffect(() => {
        if (draft && draft.value === serverValue) {
            setDraft(null);
        }
    }, [serverValue]);

    const setValue = useCallback((next: SetStateAction<T>) => {
        setDraft((previous) => {
            const current = previous ? previous.value : serverValueRef.current;
            return { value: typeof next === 'function' ? (next as (value: T) => T)(current) : next };
        });
    }, []);

    const discard = useCallback(() => setDraft(null), []);

    return {
        value: draft ? draft.value : serverValue,
        setValue,
        isDirty: !!draft && draft.value !== serverValue,
        discard,
    };
};
